import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingUp, Info } from 'lucide-react';
import { BuydownScenario } from './utils/buydownCalculations';
import { calculateBreakevenData } from './utils/breakevenCalculations';

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

interface BreakevenAnalysisChartProps {
  scenarios: BuydownScenario[];
}

export const BreakevenAnalysisChart: React.FC<BreakevenAnalysisChartProps> = ({ scenarios }) => {
  const [timeframe, setTimeframe] = useState<24 | 36 | 60>(60);
  
  const baselineScenario = scenarios.find(s => s.type === 'fixed');
  
  const breakevenData = useMemo(() => {
    if (!baselineScenario) return [];
    return calculateBreakevenData(scenarios, baselineScenario, timeframe);
  }, [scenarios, baselineScenario, timeframe]);
  
  const chartData = useMemo(() => {
    if (breakevenData.length === 0) return [];
    
    const maxMonths = timeframe;
    const result = [];
    
    for (let month = 0; month <= maxMonths; month++) {
      const point: any = { month };
      
      breakevenData.forEach(scenarioData => {
        const dataPoint = scenarioData.data[month];
        if (dataPoint) {
          point[scenarioData.scenarioType] = dataPoint.netPosition;
        }
      });
      
      result.push(point);
    }
    
    return result;
  }, [breakevenData, timeframe]);
  
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload) return null;
    
    const months = label;
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    return (
      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
        <p className="text-sm font-medium mb-2">
          {years > 0 ? `${years} year${years > 1 ? 's' : ''} ` : ''}
          {remainingMonths > 0 ? `${remainingMonths} month${remainingMonths > 1 ? 's' : ''}` : ''}
          {months === 0 ? 'Closing' : ''}
        </p>
        <div className="space-y-1">
          {payload.map((entry: any, index: number) => {
            const scenarioData = breakevenData.find(d => d.scenarioType === entry.dataKey);
            const isProfit = entry.value >= 0;
            
            return (
              <div key={index} className="space-y-0.5">
                <div className="flex items-center gap-2 text-xs">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-muted-foreground">{scenarioData?.scenarioName}:</span>
                </div>
                <div className={`text-sm font-bold ml-5 ${isProfit ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {formatCurrency(Math.abs(entry.value))} {isProfit ? 'ahead' : 'behind'}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            <CardTitle className="text-lg">Breakeven Analysis</CardTitle>
          </div>
          <Tabs value={String(timeframe)} onValueChange={(v) => setTimeframe(Number(v) as 24 | 36 | 60)}>
            <TabsList className="h-8">
              <TabsTrigger value="24" className="text-xs">2 Years</TabsTrigger>
              <TabsTrigger value="36" className="text-xs">3 Years</TabsTrigger>
              <TabsTrigger value="60" className="text-xs">5 Years</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-foreground">
              This chart shows your net position over time. Negative values mean you haven't recouped your extra upfront cost yet. 
              Positive values show total profit compared to the fixed rate option.
            </p>
          </div>
        </div>
        
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              dataKey="month" 
              label={{ value: 'Months', position: 'insideBottom', offset: -5 }}
              tick={{ fontSize: 11 }}
              tickFormatter={(value) => value % 12 === 0 ? `${value / 12}y` : ''}
            />
            <YAxis 
              label={{ value: 'Net Position', angle: -90, position: 'insideLeft' }}
              tick={{ fontSize: 11 }}
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
              formatter={(value) => breakevenData.find(d => d.scenarioType === value)?.scenarioName || value}
            />
            
            <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" strokeWidth={2} />
            
            {breakevenData.map((scenarioData) => (
              <Line 
                key={scenarioData.scenarioType}
                type="monotone" 
                dataKey={scenarioData.scenarioType}
                stroke={scenarioData.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5 }}
                name={scenarioData.scenarioName}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
        
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {breakevenData.map((scenarioData) => (
            <div 
              key={scenarioData.scenarioType}
              className="p-3 rounded-lg border-2"
              style={{ borderColor: scenarioData.color }}
            >
              <div className="text-xs text-muted-foreground mb-1">{scenarioData.scenarioName}</div>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span>Breakeven:</span>
                  <span className="font-semibold">
                    {scenarioData.breakevenMonth 
                      ? `${scenarioData.breakevenMonth} months` 
                      : 'Beyond timeline'}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span>Net at {timeframe / 12}yr:</span>
                  <span className={`font-semibold ${
                    (timeframe === 24 ? scenarioData.totalSavingsAt24Months : 
                     timeframe === 36 ? (scenarioData.data[36]?.netPosition || 0) :
                     scenarioData.totalSavingsAt60Months) >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {formatCurrency(
                      timeframe === 24 ? scenarioData.totalSavingsAt24Months : 
                      timeframe === 36 ? (scenarioData.data[36]?.netPosition || 0) :
                      scenarioData.totalSavingsAt60Months
                    )}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
